// dependencies
import { ObjectId } from "mongodb";
import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebounceValue } from "usehooks-ts";

import { faPlus, faSearch } from "@fortawesome/pro-regular-svg-icons";

import { ICustomQuestionGetResponse } from "@performyard/models";
import {
	ButtonStandard,
	ModalBase,
	type PYColumnDef,
	type SortChangedEvent,
	Table,
	Tabs,
	Text,
} from "@performyard/py-components";

// components
import { AdminTitle } from "../../../../../components/Admin";
import TextInput from "../../../../../components/Inputs/InputText";
import SelectInput from "../../../../../components/Inputs/SelectInput";
import PageTitle from "../../../../../components/PageTitle";
import Spacer from "../../../../../components/Spacer";
import Typography from "../../../../../components/Text";
import CreateOrEditCustomSurveyQuestionModal from "./components/CreateOrEditCustomSurveyQuestionModal";
import MaxQuestionsReachedModal from "./components/MaxQuestionsReachedModal";
import getSurveyQuestionsTableCols from "./components/SurveyQuestionsTableCols";
import type { SurveyQuestionsGridContext } from "./components/SurveyQuestionsTableCols";

import { SearchServiceSort } from "../../../../../api/hooks";
import { useEngagementSurveyQuestions, useMutateBuiltInQuestionStatus } from "../../../../../api/hooks/defs/engagement";
import { useEngagementCustomFactors } from "../../../../../api/hooks/defs/engagement-custom-factors";
import {
	useDeleteEngagementSurveyCustomQuestion,
	useMutateEngagementSurveyCustomQuestion,
} from "../../../../../api/hooks/defs/engagement-survey-custom-questions";
// hooks
import { useFeatureFlagQuery } from "../../../../../hooks/useFeatureFlag";

// utils
import { processQuestions } from "./utils";

// config
import Config from "../../../../Engagement/Config";
import CustomFactors from "../CustomFactors";
// css
import css from "./SurveyQuestions.module.scss";

const SurveyQuestions = () => {
	const [searchParams] = useSearchParams();

	// Feature flag for custom factors
	const { value: customFactorsEnabled } = useFeatureFlagQuery("ff-engagement-custom-factors", false);

	// Tab state - only used when custom factors is enabled
	const tabFromUrl = searchParams.get("tab");
	const activeTab = tabFromUrl === "custom-factors" ? "custom-factors" : "questions";

	// when this state is set, the delete modal will appear
	const [deleteModalQuestion, setDeleteModalQuestion] = useState<ObjectId | null>();
	const [editModalQuestion, setEditModalQuestion] = useState<ICustomQuestionGetResponse | null>(null);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [currentSort, setCurrentSort] = useState<SearchServiceSort>({
		name: "factor",
		direction: "asc",
	});
	const [search, setSearch] = useState("");
	const [searchValue] = useDebounceValue(search, 250);
	const [factor, setSelectedFactor] = useState("All Factors");

	const { isLoading, refetch, data } = useEngagementSurveyQuestions();

	// Fetch custom factors for table display (resolving IDs to names)
	const { data: customFactorsData } = useEngagementCustomFactors({ enabled: customFactorsEnabled });
	const customFactorsMap = useMemo(() => {
		const map: Record<string, string> = {};
		if (customFactorsData?.customFactors) {
			for (const f of customFactorsData.customFactors) {
				map[f._id] = f.name;
			}
		}
		return map;
	}, [customFactorsData?.customFactors]);

	const getFactors = () => {
		return [...Config.FACTORS.FACTORS, "Custom"];
	};

	const maxAllowedCustomQuestions = data?.maxAllowedCustomQuestions || 0;

	const customQuestionsCount = data?.customQuestionsCount || 0;

	const questions = useMemo(() => {
		let questions = [];
		if (data?.results) {
			questions = processQuestions(
				data.results,
				factor,
				currentSort,
				searchValue,
				customFactorsEnabled ? customFactorsMap : undefined
			);
		}
		return questions;
	}, [
		data?.results,
		currentSort.name,
		currentSort.direction,
		factor,
		searchValue,
		customFactorsEnabled,
		customFactorsMap,
	]);

	const { mutate: updateCustomQuestion } = useMutateEngagementSurveyCustomQuestion(refetch);

	const { mutate: remove } = useDeleteEngagementSurveyCustomQuestion(refetch);

	const { mutate: updateBuiltInQuestionStatus } = useMutateBuiltInQuestionStatus(refetch);

	const handleOptionsDrawer = async (key: string, question: ICustomQuestionGetResponse) => {
		const updateQuestion = (updateObject) => {
			if (question._id) {
				updateCustomQuestion({ _id: question._id, ...updateObject });
			} else {
				updateBuiltInQuestionStatus({ id: question.id, ...updateObject });
			}
		};

		switch (key) {
			case "delete":
				setDeleteModalQuestion(question._id);
				break;

			case "disable":
				updateQuestion({ enabled: false });
				break;

			case "enable":
				updateQuestion({ enabled: true });

				break;

			case "edit":
				setEditModalQuestion(question);
				break;

			default:
				break;
		}
	};

	const resetModals = () => {
		setEditModalQuestion(null);
		setIsCreateModalOpen(false);
	};

	const deleteModal = (
		<ModalBase isOpen={deleteModalQuestion != null} onClose={() => setDeleteModalQuestion(null)}>
			<div className="w-[432px] px-6 pb-6">
				<Text size="sub-heading" weight="semi-bold" dangerouslySetClassName="text-center">
					Delete Question
				</Text>
				<Text size="paragraph-1" weight="regular" dangerouslySetClassName="mt-4 text-center">
					Are you sure you want to delete this question?
				</Text>
				<div className="mt-6 flex justify-center gap-2">
					<div>
						<ButtonStandard
							variant="outline"
							color="neutral"
							size="large"
							onClick={() => setDeleteModalQuestion(null)}
						>
							Cancel
						</ButtonStandard>
					</div>
					<div>
						<ButtonStandard
							color="primary"
							size="large"
							onClick={() => {
								if (deleteModalQuestion) {
									remove(deleteModalQuestion);
									setDeleteModalQuestion(null);
								}
							}}
						>
							Yes, Delete Question
						</ButtonStandard>
					</div>
				</div>
			</div>
		</ModalBase>
	);

	const factors = getFactors();

	const dropdownFactors = [
		{ label: "All Factors", value: "All Factors" },
		...factors.sort().map((factor) => ({
			label: factor,
			value: factor,
		})),
	];

	const onFactorChanged = (factor) => {
		setSelectedFactor(factor.value);
	};

	const onSortChanged = (event: SortChangedEvent<ICustomQuestionGetResponse>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn && sortedColumn.colId) {
			setCurrentSort({
				name: sortedColumn.colId,
				direction: (sortedColumn.sort as "asc" | "desc") || "asc",
			});
		}
	};

	const columnDefs: PYColumnDef<ICustomQuestionGetResponse>[] = useMemo(() => getSurveyQuestionsTableCols(), []);

	const gridContext: SurveyQuestionsGridContext = useMemo(
		() => ({
			handleOptionsDrawer,
			customFactorsEnabled,
			customFactorsMap,
		}),
		[handleOptionsDrawer, customFactorsEnabled, customFactorsMap]
	);

	const emptyOverlay = useMemo(() => {
		if (searchValue) {
			const NoSearchResults = () => (
				<div className={css.emptyState}>
					<Typography size="paragraph-1" weight="semi-bold">
						There are no results that match your search.
					</Typography>
				</div>
			);
			return NoSearchResults;
		}
		const NoCustomQuestions = () => (
			<div className={css.emptyState}>
				<Typography size="paragraph-1" weight="semi-bold">
					You currently have no custom questions.
				</Typography>
				<Typography size="paragraph-1" weight="semi-bold">
					Create a custom question to begin.
				</Typography>
				<Spacer height={1} />
				<ButtonStandard onClick={() => setIsCreateModalOpen(true)} color="primary" iconLeft={faPlus}>
					Custom Question
				</ButtonStandard>
			</div>
		);
		return NoCustomQuestions;
	}, [searchValue]);

	// Questions tab content - reused whether tabs are shown or not
	const questionsContent = (
		<>
			{customFactorsEnabled && <Spacer height={3} />}
			<div className={css.tableControls}>
				<TextInput
					className={css.search}
					value={search}
					onChange={setSearch}
					placeholder="Search fields..."
					icon={faSearch}
					clearable
				/>
				<div className={css.headerRight}>
					<div className={css.factorFilter}>
						<label htmlFor="factor-filter">
							<Typography size="caption" weight="semi-bold">
								Filter by Factor
							</Typography>
						</label>
						<div className={css.select}>
							<SelectInput
								inputId="factor-filter"
								results={dropdownFactors}
								onChange={onFactorChanged}
								value={factor}
								isSearchable={false}
								className={css.selectInput}
							/>
						</div>
					</div>
					<div>
						<ButtonStandard onClick={() => setIsCreateModalOpen(true)} color="primary" iconLeft={faPlus}>
							Custom Question
						</ButtonStandard>
					</div>
				</div>
			</div>
			<Spacer height={2} />
			<div className={css.table}>
				<Table<ICustomQuestionGetResponse>
					autoHeight
					rowData={questions}
					columnDefs={columnDefs}
					loading={isLoading || (customFactorsEnabled && !customFactorsData)}
					getRowId={(params) => String(params.data._id ?? params.data.id)}
					emptyStateText="No Questions Found"
					noRowsOverlayComponent={emptyOverlay}
					rowHeight={48}
					context={gridContext}
					onSortChanged={onSortChanged}
					initialState={{
						sort: {
							sortModel: [{ colId: "factor", sort: "asc" }],
						},
					}}
				/>
			</div>
			{deleteModal}
			<CreateOrEditCustomSurveyQuestionModal
				isOpen={
					editModalQuestion !== null ||
					(isCreateModalOpen && customQuestionsCount < maxAllowedCustomQuestions)
				}
				onClose={resetModals}
				onSave={() => {
					resetModals();
					refetch();
				}}
				selectedQuestion={editModalQuestion || undefined}
			/>
			<MaxQuestionsReachedModal
				isOpen={customQuestionsCount >= maxAllowedCustomQuestions && isCreateModalOpen}
				maxAllowedQuestions={maxAllowedCustomQuestions}
				onClose={resetModals}
			/>
		</>
	);

	// When custom factors feature is enabled, show tabs
	// Otherwise, show the questions content directly (original behavior)
	return (
		<div className={css.main}>
			<AdminTitle>{customFactorsEnabled ? "Questions & Factors" : "Questions"}</AdminTitle>
			<PageTitle title={customFactorsEnabled ? "Questions & Factors" : "Questions"} />
			{customFactorsEnabled ? <Spacer height={3} /> : <Spacer height={4} />}
			{customFactorsEnabled ? (
				<Tabs
					tabs={[
						{ value: "questions", label: "Questions", content: questionsContent },
						{ value: "custom-factors", label: "Custom Factors", content: <CustomFactors /> },
					]}
					value={activeTab}
					urlParam="tab"
					color="neutral"
					size="large"
				/>
			) : (
				questionsContent
			)}
		</div>
	);
};

export default SurveyQuestions;
